import { CopyOutlined, CopyTwoTone } from "@ant-design/icons";
import { Button, Empty, Typography } from "antd";
import { useUpdatePayoutMutation } from "api/TenantSlug/Payout/hooks.ts";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerSubCollapsible } from "components/UI/DrawerCollapsible/DrawerSubCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { PermissionEnum, PayoutResult } from "types/enums.ts";
import { FormId } from "types/models.ts";
import { z } from "zod";

import PayoutData = App.Data.PayoutData;
import UpdatePayoutRequest = App.Http.Requests.Payout.UpdatePayoutRequest;

interface LogTransactionsSectionProps {
    payouts: PayoutData[];
    loading?: boolean;
}

const schema = z.object({
    id: z.string(),
    result: z.nativeEnum(PayoutResult).optional(),
    amount: z.number().optional().nullable(),
    affiliate_payout_id: z.string().optional().nullable(),
    affiliate_amount: z.number().optional().nullable(),
});

type FormData = z.infer<typeof schema>;

interface PayoutGroupFormProps {
    payoutData: PayoutData;
    affiliatePayout: PayoutData | null;
    formId: string;
}

const PayoutGroupForm: FC<PayoutGroupFormProps> = ({ payoutData, affiliatePayout, formId }) => {
    const { t } = usePaldockTranslation("forms.transaction-report-edit");
    const [payout, setPayout] = useState(payoutData);
    const { hasPermission } = usePermission();
    const canUpdatePayout = hasPermission(PermissionEnum.PayoutUpdate);

    const updateAdvertiserPayoutMutation = useUpdatePayoutMutation();
    const updateAffiliatePayoutMutation = useUpdatePayoutMutation();

    const handleSubmit = async (data: FormData) => {
        if (!canUpdatePayout) return;
        const mutations: Promise<unknown>[] = [];

        mutations.push(
            updateAdvertiserPayoutMutation.mutateAsync({
                payoutId: payout.id,
                request: {
                    amount: data.amount ?? undefined,
                    result: data.result,
                } as UpdatePayoutRequest,
            })
        );

        if (affiliatePayout && data.affiliate_amount !== undefined && data.affiliate_amount !== null) {
            mutations.push(
                updateAffiliatePayoutMutation.mutateAsync({
                    payoutId: affiliatePayout.id,
                    request: {
                        amount: data.affiliate_amount,
                    } as UpdatePayoutRequest,
                })
            );
        }

        try {
            await Promise.all(mutations);
            toast.success(t("messages.update_success"));
        } catch {
            // handled by react-query
        }
    };

    const defaultValues: FormData = {
        ...payout,
        affiliate_payout_id: affiliatePayout?.id ?? null,
        affiliate_amount: affiliatePayout?.amount ?? null,
    };

    return (
        <GeneralFormProvider<FormData>
            key={payout.id}
            onSubmit={handleSubmit}
            schema={schema}
            defaultValues={defaultValues}
            id={formId}
            resetOnSubmit={false}
        >
            <PayoutGroupFormContent
                payoutData={payout}
                affiliatePayout={affiliatePayout}
                setPayout={setPayout}
                canUpdatePayout={canUpdatePayout}
            />
        </GeneralFormProvider>
    );
};

interface PayoutGroupFormContentProps {
    payoutData: PayoutData;
    affiliatePayout: PayoutData | null;
    setPayout: (p: PayoutData) => void;
    canUpdatePayout: boolean;
}

const PayoutGroupFormContent: FC<PayoutGroupFormContentProps> = ({ payoutData, affiliatePayout, canUpdatePayout }) => {
    const { t } = usePaldockTranslation("forms.transaction-report-edit");
    const { t: tG } = usePaldockTranslation("general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tColumns } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const { control } = useGeneralForm<FormData>();

    const list = PropertiesList.create(payoutData, [
        {
            term: t("fields.transaction_id.label"),
            description: ({ id }) => id,
            render: (payout) => (
                <span className="flex items-center gap-1">
                    <EntityLinkCell.Transaction entity={payout} />
                    <Typography.Text
                        copyable={{
                            text: payout.id,
                            icon: [<CopyOutlined key="copy" />, <CopyTwoTone key="copied" />],
                            tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                        }}
                    />
                </span>
            ),
        },
        {
            term: tColumns("commission_id"),
            description: ({ commission_id }) => commission_id,
            render: (payout) =>
                payout.commission ? (
                    <span className="flex items-center gap-1">
                        <EntityLinkCell.Commission entity={payout.commission} />
                        <Typography.Text
                            copyable={{
                                text: payout.commission_id,
                                icon: [<CopyOutlined key="copy" />, <CopyTwoTone key="copied" />],
                                tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                            }}
                        />
                    </span>
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("conversion_id"),
            description: ({ conversionState }) => conversionState?.eid,
            render: ({ conversionState }) =>
                conversionState?.eid ? (
                    <ShortenedIdWithCopy id={conversionState.eid} charactersBetweenDots={35} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("origin_id"),
            description: ({ origin_eid }) => origin_eid,
            render: ({ origin_eid }) => origin_eid ? <ShortenedIdWithCopy id={origin_eid} charactersBetweenDots={35} /> : <EmptyCell />,
        },
        {
            term: t("fields.external_id.label"),
            description: ({ conversionState }) => conversionState?.advertiser_custom_id,
            render: ({ conversionState }) =>
                conversionState?.advertiser_custom_id ? (
                    <ShortenedIdWithCopy id={conversionState.advertiser_custom_id} charactersBetweenDots={35} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tFields("transaction_type.label"),
            description: (payout) => payout.type ?? <EmptyCell />,
        },
        {
            term: tFields("advertiser.label"),
            description: ({ conversionState }) => conversionState?.advertiser?.nickname ?? conversionState?.advertiser?.email,
            render: ({ conversionState }) =>
                conversionState?.advertiser ? <EntityLinkCell.Advertiser entity={conversionState.advertiser} /> : <EmptyCell />,
        },
        {
            term: tFields("channel.label"),
            description: ({ conversionState }) => conversionState?.cascade?.visible_title ?? conversionState?.cascade?.title,
            render: ({ conversionState }) =>
                conversionState?.cascade ? <EntityLinkCell.Cascade entity={conversionState.cascade} /> : <EmptyCell />,
        },
        {
            term: tFields("created_at.label"),
            description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
        },
        {
            term: t("fields.origin_date.label"),
            description: ({ conversionState }) =>
                conversionState?.created_at ? <DateTimeCell value={conversionState.created_at} /> : <EmptyCell />,
        },
        {
            term: t("fields.affiliate_payout.label"),
            render: affiliatePayout ? (
                <InteractiveCell.Money
                    formControl={{ control, name: "affiliate_amount" }}
                    currencyCode={affiliatePayout.currency?.currency_code}
                />
            ) : (
                <EmptyCell />
            ),
        },
        {
            term: t("fields.workspace_payout.label"),
            render: (
                <InteractiveCell.Money
                    formControl={{ control, name: "amount" }}
                    currencyCode={payoutData.currency?.currency_code}
                />
            ),
        },
        {
            term: t("fields.result.label"),
            render: canUpdatePayout ? (
                <InteractiveCell.Select
                    options={[
                        { value: PayoutResult.Approved, label: tG("payout_result.approved") },
                        { value: PayoutResult.Rejected, label: tG("payout_result.rejected") },
                        { value: PayoutResult.Pending, label: tG("payout_result.pending") },
                    ]}
                    formControl={{ control, name: "result" }}
                />
            ) : (
                payoutData.result ?? <EmptyCell />
            ),
        },
    ]);

    return (
        <>
            <PropertiesList list={list} />
            {canUpdatePayout && (
                <div className="mt-2 flex justify-end">
                    <Button type="primary" htmlType="submit" size="small">
                        {tC("save")}
                    </Button>
                </div>
            )}
        </>
    );
};

export const LogTransactionsSection: FC<LogTransactionsSectionProps> = ({ payouts, loading }) => {
    const { t } = usePaldockTranslation("drawers.log-conversion-detail");

    const { parents, childrenByParentId } = useMemo(() => {
        const payoutIds = new Set(payouts.map((p) => p.id));
        const parents = payouts.filter((p) => !p.parent_id || !payoutIds.has(p.parent_id));
        const childrenByParentId = payouts
            .filter((p) => p.parent_id && payoutIds.has(p.parent_id))
            .reduce<Record<string, PayoutData[]>>((acc, child) => {
                const key = child.parent_id!;
                (acc[key] ??= []).push(child);
                return acc;
            }, {});
        return { parents, childrenByParentId };
    }, [payouts]);

    return (
        <DrawerCollapsible title={t("sections.transactions")}>
            {!loading && payouts.length === 0 ? (
                <Empty description={t("messages.no_transactions")} />
            ) : (
                parents.map((parent) => {
                    const affiliatePayout = childrenByParentId[parent.id]?.[0] ?? null;
                    return (
                        <DrawerSubCollapsible
                            key={parent.id}
                            title={<DateTimeCell value={parent.created_at} />}
                        >
                            <PayoutGroupForm
                                formId={`${FormId.ConversionPayoutGroupForm}-${parent.id}`}
                                payoutData={parent}
                                affiliatePayout={affiliatePayout}
                            />
                        </DrawerSubCollapsible>
                    );
                })
            )}
        </DrawerCollapsible>
    );
};
